/**
 * Contract Detail Page
 * Displays full contract details and amortization schedule
 */

import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  Menu,
  MenuItem,
  Paper,
  Select,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Edit as EditIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Delete as DeleteIcon,
  PictureAsPdf as PdfIcon,
  Autorenew as RolloverIcon,
  AccountBalance as AmendmentIcon,
  EventRepeat as ExtendIcon,
  Block as CancelContractIcon,
  EventAvailable as MatureIcon,
  AssignmentTurnedIn as FinaliseIcon,
  ArrowDropDown as ArrowDropDownIcon,
  MergeType as MergeIcon,
  Tune as OverrideIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { apiGet, apiPost, apiPut, apiDelete } from '../utils/apiClient';
import { usePermissions } from '../contexts/PermissionContext';
import { useNotification } from '../contexts/NotificationContext';
import useConfirmDialog from '../hooks/useConfirmDialog';
import ConfirmDialog from '../components/common/ConfirmDialog';
import { MODULES, PERMISSION_LEVELS } from '../contexts/PermissionConstants';
import AmortizationScheduleTable from '../components/contracts/AmortizationScheduleTable';
import ContractInvestorsSection from '../components/contracts/ContractInvestorsSection';
import RateChangePreviewModal from '../components/contracts/RateChangePreviewModal';
import AdhocCapitalAmendmentDialog from '../components/contracts/AdhocCapitalAmendmentDialog';
import AdhocInterestPaymentDialog from '../components/contracts/AdhocInterestPaymentDialog';
import AdhocRateChangeDialog from '../components/contracts/AdhocRateChangeDialog';
import RateChangeModeDialog from '../components/contracts/RateChangeModeDialog';
import ExtendContractDialog from '../components/contracts/ExtendContractDialog';
import FinalisationDialog from '../components/contracts/FinalisationDialog';
import ReservePayoutDialog from '../components/contracts/ReservePayoutDialog';
import ScheduleEntryOverrideDialog from '../components/contracts/ScheduleEntryOverrideDialog';
import ScheduleOverridesModal from '../components/contracts/ScheduleOverridesModal';
import { DocumentUploadSection } from '../components/documents';
import useServerPDF from '../hooks/useServerPDF';
import { formatCurrency as formatCurrencyBase } from '../utils/formatters';
import { formatNumber } from '../utils/validation';
import { formatRatePct, formatRateType, formatRateChangeBasis, formatBrokerFee } from '../utils/formatters';
import TabPanel from '../components/common/TabPanel';
import MergeHistoricalDialog from '../components/common/MergeHistoricalDialog';
import HistoricalBalanceOverrideDialog from '../components/common/HistoricalBalanceOverrideDialog';
import { useTenant } from '../contexts/TenantContext';
import logger from '../utils/logger';

// Extra contract-level documents beyond the always-present Contract/Amortization
// buttons. `type` = pdf_templates.template_type; `route` = the /generate-pdf/:route
// segment consumed by useServerPDF (matches the API route names). Module-scope so
// it is a stable reference across renders (safe as a useEffect dependency).
const EXTRA_DOC_TYPES = [
  { type: 'interest_letter', route: 'interest-letter', label: 'Interest Letter', filePrefix: 'Interest_Letter' },
  { type: 'pricing_supplement', route: 'pricing-supplement', label: 'Pricing Supplement', filePrefix: 'Pricing_Supplement' },
  { type: 'aps', route: 'aps', label: 'Applicable Pricing Supplement (APS)', filePrefix: 'APS' },
  { type: 'portfolio_agreement', route: 'portfolio-agreement', label: 'Portfolio Agreement', filePrefix: 'Portfolio_Agreement' },
];

function ContractDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasPermission, canApprove, currentUserEmail } = usePermissions();
  const notify = useNotification();
  const confirm = useConfirmDialog();
  const { tenant, getStakeholderLabel } = useTenant();
  const issuerLabel = getStakeholderLabel('stakeholder.issuer.label', 'Issuer');
  const dp = tenant?.roundingPrecision ?? 3;
  const formatCurrency = (value, currencyCode) => formatCurrencyBase(value, currencyCode, dp);

  const [loading, setLoading] = useState(true);
  const [contract, setContract] = useState(null);
  const [schedule, setSchedule] = useState([]);
  const [rolloverSources, setRolloverSources] = useState([]);  // T3-032 many-to-1 sources
  const [approvalHistory, setApprovalHistory] = useState([]);
  const [investors, setInvestors] = useState([]);
  const [payments, setPayments] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [scheduleVersions, setScheduleVersions] = useState([]);
  const [adhocCapitalEvents, setAdhocCapitalEvents] = useState([]);
  const [varianceSummary, setVarianceSummary] = useState(null);
  const [paymentVariances, setPaymentVariances] = useState([]);

  // schedule_id → variance row, used by AmortizationScheduleTable to render the
  // per-row variance chip. Built off the /payment-variances feed so the
  // schedule generation path stays untouched.
  const varianceById = useMemo(() => {
    const m = new Map();
    for (const v of paymentVariances) {
      if (v?.schedule_id != null) m.set(v.schedule_id, v);
    }
    return m;
  }, [paymentVariances]);
  const [tabValue, setTabValue] = useState(0);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [approveComments, setApproveComments] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [historicalDeleteDialogOpen, setHistoricalDeleteDialogOpen] = useState(false);
  const [historicalDeleteError, setHistoricalDeleteError] = useState(null);
  const [historicalDeleting, setHistoricalDeleting] = useState(false);
  const [finalisationDialogOpen, setFinalisationDialogOpen] = useState(false);
  const [reservePayoutDialogOpen, setReservePayoutDialogOpen] = useState(false);
  const [reserveRemaining, setReserveRemaining] = useState(0);
  const [pendingRateChange, setPendingRateChange] = useState(null);
  const [rateChangePreviewOpen, setRateChangePreviewOpen] = useState(false);
  const [amendmentDialogOpen, setAmendmentDialogOpen] = useState(false);
  // Which event type the capital amendment dialog opens in, chosen from the Capital Amendments dropdown.
  const [amendmentDialogEventType, setAmendmentDialogEventType] = useState('withdrawal');
  // Anchor for the "Capital Amendments" dropdown (Capital Injection / Capital Withdrawal).
  const [capitalMenuAnchor, setCapitalMenuAnchor] = useState(null);
  const [overrideDialogOpen, setOverrideDialogOpen] = useState(false);
  const [selectedOverrideEntry, setSelectedOverrideEntry] = useState(null);
  const [overridesModalOpen, setOverridesModalOpen] = useState(false);
  const [overridesModalEntry, setOverridesModalEntry] = useState(null);
  const [extendDialogOpen, setExtendDialogOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelSubmitting, setCancelSubmitting] = useState(false);
  const [matureSubmitting, setMatureSubmitting] = useState(false);
  const [adhocInterestDialogOpen, setAdhocInterestDialogOpen] = useState(false);
  // Which mode the interest dialog opens in, chosen from the Interest Amendments dropdown.
  const [adhocInterestDialogMode, setAdhocInterestDialogMode] = useState('payment');
  // Anchor for the "Interest Amendments" dropdown (Payment / Accrual Adjustment / Rate Change).
  const [interestMenuAnchor, setInterestMenuAnchor] = useState(null);
  const [adhocRateChangeDialogOpen, setAdhocRateChangeDialogOpen] = useState(false);
  const [rateChangeModeDialogOpen, setRateChangeModeDialogOpen] = useState(false);
  const [requireActivation, setRequireActivation] = useState(false);
  const [allowDeleteHistorical, setAllowDeleteHistorical] = useState(false);
  // T3-075: admin Historical toolkit toggles + dialog state (merge + balance override).
  const [allowHistoricalMerge, setAllowHistoricalMerge] = useState(false);
  const [allowHistoricalBalanceOverride, setAllowHistoricalBalanceOverride] = useState(false);
  const [mergeHistoricalDialogOpen, setMergeHistoricalDialogOpen] = useState(false);
  const [balanceOverrideDialogOpen, setBalanceOverrideDialogOpen] = useState(false);
  const [selectedVersionId, setSelectedVersionId] = useState(null);
  const [versionSchedule, setVersionSchedule] = useState(null);
  const [loadingVersion, setLoadingVersion] = useState(false);
  // "Generate Document" menu: anchor + the set of doc types that currently have an
  // active template (so we only offer documents that can actually be produced).
  const [genMenuAnchor, setGenMenuAnchor] = useState(null);
  const [availableDocTypes, setAvailableDocTypes] = useState({});

  // Mirror of "is any action/preview modal open" so the window-focus refresh can
  // skip while the user is mid-action (refetching underneath an open modal is
  // what caused the flicker). A ref avoids re-binding the focus listener.
  const anyModalOpen = approveDialogOpen || rejectDialogOpen || deleteDialogOpen
    || historicalDeleteDialogOpen || mergeHistoricalDialogOpen || balanceOverrideDialogOpen
    || finalisationDialogOpen || rateChangePreviewOpen || amendmentDialogOpen
    || overrideDialogOpen || overridesModalOpen || extendDialogOpen || cancelDialogOpen
    || adhocInterestDialogOpen || adhocRateChangeDialogOpen || rateChangeModeDialogOpen;
  const anyModalOpenRef = useRef(anyModalOpen);
  anyModalOpenRef.current = anyModalOpen;

  // Guards against overlapping volatile-refresh fan-outs on rapid tab-refocus (see refreshVolatile).
  const refreshVolatileInFlightRef = useRef(false);

  const canWrite = hasPermission(MODULES.CONTRACT_MANAGEMENT, PERMISSION_LEVELS.WRITE);
  const canAdmin = hasPermission(MODULES.CONTRACT_MANAGEMENT, PERMISSION_LEVELS.ADMIN);
  const canActivate = canApprove(MODULES.CONTRACT_MANAGEMENT);
  const canApproveContract = canApprove(MODULES.CONTRACT_MANAGEMENT);

  // Server-side PDF for the contract document; client-side remains for amortization
  // schedule (pending its own migration — fn_contract_get_for_amortization_pdf has the
  // same broken-column issues that T007 fixed for fn_contract_get_for_pdf).
  const { downloadPDF, isGenerating } = useServerPDF();

  useEffect(() => {
    loadContractDetails();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- loadContractDetails is also called from multiple action handlers
  }, [id]);

  const loadContractDetails = async () => {
    try {
      setLoading(true);
      const [contractData, scheduleData, historyData, investorsData, paymentsData, receiptsData, versionsData, adhocData, rateChangeData, workflowSettings, varianceData, paymentVarianceListData, rolloverSourcesData] = await Promise.all([
        apiGet(`/contracts/${id}`),
        apiGet(`/amortization/contract/${id}`).catch(() => []),
        apiGet(`/contracts/${id}/approval-history`).catch(() => ({ data: [] })),
        apiGet(`/contracts/${id}/investors`).catch(() => ({ data: [] })),
        apiGet(`/payments/contract/${id}`).catch(() => ({ data: [] })),
        apiGet(`/payments/receipts?contract_id=${id}`).catch(() => ({ data: [] })),
        apiGet(`/contracts/${id}/schedule-versions`).catch(() => ({ data: [] })),
        apiGet(`/contracts/${id}/adhoc-capital`).catch(() => ({ data: [] })),
        apiGet(`/rate-changes/contract/${id}`).catch(() => ({ data: { pending: null } })),
        apiGet('/contracts/settings/workflow').catch(() => ({ data: { requireActivation: false } })),
        apiGet(`/contracts/${id}/variance-summary`).catch(() => ({ data: null })),
        apiGet(`/contracts/${id}/payment-variances`).catch(() => ({ data: [] })),
        apiGet(`/contracts/${id}/rollover-sources`).catch(() => ({ data: [] })),  // T3-032
      ]);

      // Set workflow settings
      if (workflowSettings?.success && workflowSettings?.data) {
        setRequireActivation(workflowSettings.data.requireActivation === true);
        setAllowDeleteHistorical(workflowSettings.data.allowDeleteHistorical === true);
        setAllowHistoricalMerge(workflowSettings.data.allowHistoricalMerge === true);
        setAllowHistoricalBalanceOverride(workflowSettings.data.allowHistoricalBalanceOverride === true);
      }

      // Helper to extract data from various response formats
      const extractData = (response) => {
        if (!response) return [];
        if (response?.success && response?.data) return response.data;
        if (response?.data && Array.isArray(response.data)) return response.data;
        if (Array.isArray(response)) return response;
        return [];
      };

      // Extract schedule - amortization API returns { success, data: { contract_id, schedule, summary } }
      const extractSchedule = (response) => {
        if (!response) return [];
        // Check for nested schedule array
        if (response?.data?.schedule && Array.isArray(response.data.schedule)) {
          return response.data.schedule;
        }
        // Check for direct schedule in data
        if (response?.schedule && Array.isArray(response.schedule)) {
          return response.schedule;
        }
        // Fallback to extractData for other formats
        const data = extractData(response);
        return Array.isArray(data) ? data : [];
      };

      const loadedContract = contractData?.data || contractData || null;
      setContract(loadedContract);
      setRolloverSources(extractData(rolloverSourcesData));  // T3-032 many-to-1 sources

      // For Finalised contracts, load remaining (un-rolled) reserve so we can
      // show/enable the Pay Out Reserve action.
      if (loadedContract?.status === 'Finalised') {
        try {
          const reserveResp = await apiGet(`/contracts/${id}/reserve-summary`);
          const totalRemaining = (reserveResp?.data || []).reduce(
            (sum, r) => sum + (parseFloat(r.remaining) || 0), 0
          );
          setReserveRemaining(totalRemaining);
        } catch (reserveErr) {
          logger.error('Error loading reserve summary', { message: reserveErr.message });
          setReserveRemaining(0);
        }
      } else {
        setReserveRemaining(0);
      }

      setSchedule(extractSchedule(scheduleData));
      setApprovalHistory(historyData?.data || []);
      setInvestors(investorsData?.data || []);
      setPayments(extractData(paymentsData));
      setReceipts(extractData(receiptsData));
      setScheduleVersions(extractData(versionsData));
      setAdhocCapitalEvents(extractData(adhocData));
      setPendingRateChange(rateChangeData?.data?.pending || null);
      setVarianceSummary(varianceData?.data || null);
      setPaymentVariances(Array.isArray(paymentVarianceListData?.data) ? paymentVarianceListData.data : []);
    } catch (err) {
      logger.error('Error loading contract', { message: err.message, status: err.response?.status });
      setContract(null);
      setSchedule([]);
      setApprovalHistory([]);
      setInvestors([]);
      setPayments([]);
      setReceipts([]);
      setScheduleVersions([]);
      setAdhocCapitalEvents([]);
    } finally {
      setLoading(false);
    }
  };

  // Silent refresh of only the VOLATILE feeds — the data that can change from
  // another tab / external action while this page is open. Deliberately omits
  // the full-page spinner (no setLoading) and the static feeds (schedule,
  // investors, receipts, versions, adhoc-capital, workflow settings) so a
  // window-focus refresh doesn't blank the page or flicker open modals.
  //   - /contracts/:id        → contract.amendment_status (pending capital /
  //                             interest / finalisation / extension amendments)
  //   - /rate-changes/contract → pending rate change banner + approve CTA
  //   - payments / variance feed / approval-history → matched/approved elsewhere
  const refreshVolatile = useCallback(async () => {
    // In-flight guard: this fires a 6-call fan-out on every tab-refocus. Without a guard,
    // rapid alt-tabbing (faster than a slow tenant pool resolves) can stack multiple
    // concurrent batches. Skip a new batch until the previous one settles. The 150ms
    // debounce on the focus listener only collapses events within 150ms — this covers
    // the slow-response case the debounce can't.
    if (refreshVolatileInFlightRef.current) return;
    refreshVolatileInFlightRef.current = true;
    try {
      const pick = (response) => {
        if (!response) return [];
        if (response?.success && response?.data) return response.data;
        if (response?.data && Array.isArray(response.data)) return response.data;
        if (Array.isArray(response)) return response;
        return [];
      };
      const [contractData, paymentsData, varianceData, paymentVarianceListData, historyData, rateChangeData] = await Promise.all([
        apiGet(`/contracts/${id}`).catch(() => null),
        apiGet(`/payments/contract/${id}`).catch(() => ({ data: [] })),
        apiGet(`/contracts/${id}/variance-summary`).catch(() => ({ data: null })),
        apiGet(`/contracts/${id}/payment-variances`).catch(() => ({ data: [] })),
        apiGet(`/contracts/${id}/approval-history`).catch(() => ({ data: [] })),
        apiGet(`/rate-changes/contract/${id}`).catch(() => ({ data: { pending: null } })),
      ]);
      if (contractData?.data || contractData) setContract(contractData?.data || contractData);
      setPayments(pick(paymentsData));
      setVarianceSummary(varianceData?.data || null);
      setPaymentVariances(Array.isArray(paymentVarianceListData?.data) ? paymentVarianceListData.data : []);
      setApprovalHistory(historyData?.data || []);
      setPendingRateChange(rateChangeData?.data?.pending || null);
    } catch (err) {
      logger.error('Volatile refresh failed', { message: err.message });
    } finally {
      refreshVolatileInFlightRef.current = false;
    }
  }, [id]);

  // When the user returns to this tab/window after acting elsewhere (matching a
  // payment, approving an amendment), silently refresh only the VOLATILE feeds
  // so the variance/payment tiles and the pending-amendment banner reflect the
  // new state — without the full-page spinner or reloading static data, and
  // WITHOUT refetching underneath an open modal (which caused the flicker).
  useEffect(() => {
    let timer = null;
    const onFocus = () => {
      if (document.hidden) return;
      if (anyModalOpenRef.current) return; // don't refetch under an open modal
      // Debounce: focus + visibilitychange can both fire on a single tab return.
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => { refreshVolatile(); }, 150);
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      if (timer) clearTimeout(timer);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [refreshVolatile]);

  const handleApprove = async () => {
    try {
      await apiPost(`/contracts/${id}/approve`, { comments: approveComments });
      setApproveDialogOpen(false);
      setApproveComments('');
      await loadContractDetails();
    } catch (err) {
      logger.error('Error approving contract', { message: err.message, status: err.response?.status });
      notify.error('Failed to approve contract: ' + (err.response?.data?.error?.message || err.response?.data?.message || err.message));
    }
  };

  const handleReject = async () => {
    try {
      await apiPost(`/contracts/${id}/reject`, { reason: rejectReason });
      setRejectDialogOpen(false);
      navigate('/contracts');
    } catch (err) {
      logger.error('Error rejecting contract', { message: err.message, status: err.response?.status });
    }
  };

  const handleDelete = async () => {
    try {
      await apiDelete(`/contracts/${id}`);
      navigate('/contracts');
    } catch (err) {
      logger.error('Error deleting contract', { message: err.message, status: err.response?.status });
    }
  };

  const handleDeleteHistorical = async () => {
    setHistoricalDeleteError(null);
    setHistoricalDeleting(true);
    try {
      await apiDelete(`/contracts/${id}/historical`);
      navigate('/contracts');
    } catch (err) {
      // Surface the server message (wrong status / referenced as rolled-from
      // source / toggle disabled) directly rather than masking the failure.
      const apiMessage = err.response?.data?.error?.message
        || err.response?.data?.message
        || err.message
        || 'Failed to delete historical contract.';
      logger.error('Error deleting historical contract', { message: err.message, status: err.response?.status });
      setHistoricalDeleteError(apiMessage);
    } finally {
      setHistoricalDeleting(false);
    }
  };

  const handleActivate = async () => {
    try {
      await apiPost(`/contracts/${id}/activate`);
      await loadContractDetails();
    } catch (err) {
      logger.error('Error activating contract', { message: err.message, status: err.response?.status });
      notify.error('Failed to activate contract: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleCancelContract = async () => {
    setCancelSubmitting(true);
    try {
      await apiPost(`/contracts/${id}/cancel`, { reason: cancelReason });
      setCancelDialogOpen(false);
      setCancelReason('');
      await loadContractDetails();
    } catch (err) {
      logger.error('Error cancelling contract', { message: err.message, status: err.response?.status });
      notify.error('Failed to cancel contract: ' + (err.response?.data?.message || err.message));
    } finally {
      setCancelSubmitting(false);
    }
  };

  const handleMature = () => {
    confirm.show({
      title: 'Mark as Matured?',
      message: 'Mark this contract as Matured? This is normally done automatically by the daily job.',
      confirmText: 'Mark Matured',
      confirmColor: 'primary',
      onConfirm: async () => {
        setMatureSubmitting(true);
        try {
          await apiPost(`/contracts/${id}/mature`);
          await loadContractDetails();
        } catch (err) {
          logger.error('Error maturing contract', { message: err.message, status: err.response?.status });
          notify.error('Failed to mature contract: ' + (err.response?.data?.message || err.message));
        } finally {
          setMatureSubmitting(false);
        }
      },
    });
  };

  const handleRegenerateSchedule = async () => {
    // The actual regeneration; runs directly when there are no applied overrides,
    // or from the confirm dialog's onConfirm when the user accepts discarding them.
    const doRegen = async () => {
      try {
        const response = await apiPost(`/amortization/regenerate/${id}`);
        // Extract data from wrapped response
        const scheduleData = response?.data?.schedule || response?.data || response?.schedule || response;
        setSchedule(Array.isArray(scheduleData) ? scheduleData : []);
        // Reload contract to get updated schedule
        await loadContractDetails();
      } catch (err) {
        logger.error('Error regenerating schedule', { message: err.message, status: err.response?.status });
      }
    };

    // Warn if applied (approved) overrides exist — they will be discarded by the regen.
    try {
      const overridesResp = await apiGet(`/amendments/contract/${id}/overrides`);
      const overrides = overridesResp?.data || overridesResp || [];
      const appliedCount = (Array.isArray(overrides) ? overrides : [])
        .filter(o => o.status === 'approved').length;
      if (appliedCount > 0) {
        confirm.show({
          title: 'Regenerate schedule?',
          message: `This contract has ${appliedCount} applied schedule override${appliedCount === 1 ? '' : 's'}. `
            + 'Regenerating will discard them. Continue?',
          confirmText: 'Regenerate',
          onConfirm: doRegen,
        });
        return;
      }
    } catch (overrideErr) {
      logger.warn('Could not check schedule overrides before regen', { message: overrideErr.message });
    }

    await doRegen();
  };

  const handleVersionChange = async (versionId) => {
    if (!versionId || versionId === 'current') {
      setSelectedVersionId(null);
      setVersionSchedule(null);
      return;
    }
    try {
      setLoadingVersion(true);
      setSelectedVersionId(versionId);
      const response = await apiGet(`/contracts/${id}/schedule-versions/${versionId}/schedule`);
      const data = response?.data || response || [];
      // Normalize column names to match main schedule format
      const normalized = (Array.isArray(data) ? data : []).map(row => ({
        ...row,
        narration: row.narration_text || row.narration,
        outstanding_balance: row.balance != null ? row.balance : row.outstanding_balance,
      }));
      setVersionSchedule(normalized);
    } catch (err) {
      logger.error('Error loading version schedule', { message: err.message, status: err.response?.status });
      setSelectedVersionId(null);
      setVersionSchedule(null);
    } finally {
      setLoadingVersion(false);
    }
  };

  const handleDownloadContractPDF = async () => {
    if (!id) return;
    try {
      const filename = `Contract_${contract?.reference || id}_${format(new Date(), 'yyyyMMdd')}.pdf`;
      await downloadPDF({ type: 'contract', id, filename });
    } catch (err) {
      logger.error('Error downloading contract PDF', { message: err.message });
      notify.error('Failed to generate contract PDF: ' + (err.message || 'Please try again.'));
    }
  };

  const handleDownloadAmortizationPDF = async () => {
    if (!id) return;
    try {
      // Server-side generation from the active 'amortization_schedule' template
      // (pdf_templates). The API rebuilds the schedule from the DB via
      // fn_amortization_schedule_get_for_pdf, so no client-side row mapping is needed.
      const filename = `Amortization_Schedule_${contract?.reference || id}_${format(new Date(), 'yyyyMMdd')}.pdf`;
      await downloadPDF({ type: 'amortization', id, filename });
    } catch (err) {
      logger.error('Error downloading amortization PDF', { message: err.message });
      notify.error('Failed to generate amortization schedule PDF: ' + (err.message || 'Please try again.'));
    }
  };

  // Which of the extra doc types have an active template? Checked once when the
  // contract id is known so the menu only offers documents that can be produced.
  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    (async () => {
      const entries = await Promise.all(
        EXTRA_DOC_TYPES.map(async ({ type }) => {
          try {
            const res = await apiGet(`/pdf-templates/type/${type}/active`);
            const tmpl = res?.data ?? res;
            return [type, !!(tmpl && (Array.isArray(tmpl) ? tmpl.length : tmpl.id))];
          } catch {
            return [type, false];
          }
        })
      );
      if (!cancelled) setAvailableDocTypes(Object.fromEntries(entries));
    })();
    return () => { cancelled = true; };
  }, [id]);

  const handleGenerateDocument = async ({ route, filePrefix }) => {
    setGenMenuAnchor(null);
    if (!id) return;
    try {
      const filename = `${filePrefix}_${contract?.reference || id}_${format(new Date(), 'yyyyMMdd')}.pdf`;
      await downloadPDF({ type: route, id, filename });
    } catch (err) {
      logger.error('Error generating document PDF', { message: err.message, route });
      notify.error('Failed to generate document: ' + (err.message || 'Please try again.'));
    }
  };

  const enabledDocTypes = EXTRA_DOC_TYPES.filter((d) => availableDocTypes[d.type]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!contract) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography variant="h6">Contract not found</Typography>
        <Button onClick={() => navigate('/contracts')} sx={{ mt: 2 }}>
          Back to Contracts
        </Button>
      </Box>
    );
  }

  const statusColors = {
    'Draft': 'default',
    'Pending Approval': 'warning',
    'Approved': 'info',
    'Active': 'success',
    'Matured': 'secondary',
    'Matured - Rolled': 'default',
    'Matured - Terminated': 'error',
    'Cancelled': 'error',
    'Early Terminated': 'error',
    'Finalised': 'info',
  };

  const isRolledOver = !!contract.rolled_from_contract_id;
  const isExtended = scheduleVersions.some(v => v.reason && /extend/i.test(v.reason));

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => navigate('/contracts')}>
            <BackIcon />
          </IconButton>
          <div>
            <Typography variant="h4">{contract.user_reference || contract.reference}</Typography>
            {contract.user_reference && (
              <Typography variant="body2" color="textSecondary">
                {contract.reference}
              </Typography>
            )}
            <Typography variant="body2" color="textSecondary">
              Created {format(new Date(contract.created_at), 'dd MMM yyyy')} by {contract.created_by}
            </Typography>
          </div>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Chip
            label={contract.status}
            color={statusColors[contract.status] || 'default'}
            sx={{ fontWeight: 'medium' }}
          />
          {isRolledOver && (
            <Chip
              label="Rolled Over"
              size="small"
              color="info"
              variant="outlined"
              icon={<RolloverIcon />}
            />
          )}
          {isExtended && (
            <Chip
              label="Extended"
              size="small"
              color="primary"
              variant="outlined"
              icon={<ExtendIcon />}
            />
          )}
          <Button
            variant="outlined"
            startIcon={isGenerating ? <CircularProgress size={20} /> : <PdfIcon />}
            onClick={handleDownloadContractPDF}
            color="primary"
            disabled={isGenerating}
          >
            {isGenerating ? 'Generating...' : 'Download PDF'}
          </Button>
          {enabledDocTypes.length > 0 && (
            <>
              <Button
                variant="outlined"
                color="primary"
                endIcon={<ArrowDropDownIcon />}
                startIcon={<PdfIcon />}
                onClick={(e) => setGenMenuAnchor(e.currentTarget)}
                disabled={isGenerating}
              >
                Generate Document
              </Button>
              <Menu
                anchorEl={genMenuAnchor}
                open={Boolean(genMenuAnchor)}
                onClose={() => setGenMenuAnchor(null)}
              >
                {enabledDocTypes.map((doc) => (
                  <MenuItem key={doc.type} onClick={() => handleGenerateDocument(doc)}>
                    {doc.label}
                  </MenuItem>
                ))}
              </Menu>
            </>
          )}
          {canWrite && contract.status === 'Draft' && (
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={() => navigate(`/contracts/${id}/edit`)}
            >
              Edit
            </Button>
          )}
          {canWrite && contract.status === 'Pending Approval' && (() => {
            const isSubmitter = contract.updated_by && currentUserEmail &&
              contract.updated_by.toLowerCase().trim() === currentUserEmail.toLowerCase().trim();
            return (
              <>
                {!isSubmitter && (
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<ApproveIcon />}
                    onClick={() => setApproveDialogOpen(true)}
                  >
                    Approve
                  </Button>
                )}
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<RejectIcon />}
                  onClick={() => setRejectDialogOpen(true)}
                >
                  Reject
                </Button>
              </>
            );
          })()}
          {canActivate && contract.status === 'Approved' && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<ApproveIcon />}
              onClick={handleActivate}
            >
              Activate Contract
            </Button>
          )}
          {canApproveContract && contract.status === 'Active' && (
            <>
              {!contract.amendment_status && (
                <>
                  <Tooltip
                    title={contract.equal_installment
                      ? "Not available for equal-installment contracts — ad-hoc capital events aren't supported on this schedule type yet."
                      : ''}
                  >
                    <span>
                      <Button
                        variant="outlined"
                        color="info"
                        startIcon={<AmendmentIcon />}
                        endIcon={<ArrowDropDownIcon />}
                        onClick={(e) => setCapitalMenuAnchor(e.currentTarget)}
                        disabled={!!contract.equal_installment}
                      >
                        Capital Amendments
                      </Button>
                    </span>
                  </Tooltip>
                  <Menu
                    anchorEl={capitalMenuAnchor}
                    open={Boolean(capitalMenuAnchor)}
                    onClose={() => setCapitalMenuAnchor(null)}
                  >
                    <MenuItem
                      onClick={() => {
                        setCapitalMenuAnchor(null);
                        setAmendmentDialogEventType('injection');
                        setAmendmentDialogOpen(true);
                      }}
                    >
                      Capital Injection
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        setCapitalMenuAnchor(null);
                        setAmendmentDialogEventType('withdrawal');
                        setAmendmentDialogOpen(true);
                      }}
                    >
                      Capital Withdrawal
                    </MenuItem>
                  </Menu>
                  <Button
                    variant="outlined"
                    color="info"
                    startIcon={<AmendmentIcon />}
                    endIcon={<ArrowDropDownIcon />}
                    onClick={(e) => setInterestMenuAnchor(e.currentTarget)}
                  >
                    Interest Amendments
                  </Button>
                  <Menu
                    anchorEl={interestMenuAnchor}
                    open={Boolean(interestMenuAnchor)}
                    onClose={() => setInterestMenuAnchor(null)}
                  >
                    {/* MenuItems must be direct children of Menu (no Tooltip/span
                        wrapper — that breaks MenuList anchoring). Disabled state +
                        inline caption convey each restriction. */}
                    <MenuItem
                      onClick={() => {
                        setInterestMenuAnchor(null);
                        setAdhocInterestDialogMode('payment');
                        setAdhocInterestDialogOpen(true);
                      }}
                      sx={{ flexDirection: 'column', alignItems: 'flex-start' }}
                    >
                      Interest Payment
                    </MenuItem>
                    <MenuItem
                      disabled={!!contract.equal_installment}
                      onClick={() => {
                        setInterestMenuAnchor(null);
                        setAdhocInterestDialogMode('accrual_adjustment');
                        setAdhocInterestDialogOpen(true);
                      }}
                      sx={{ flexDirection: 'column', alignItems: 'flex-start' }}
                    >
                      Accrual Adjustment
                      {contract.equal_installment && (
                        <Typography variant="caption" color="text.secondary">
                          Not available for equal-installment contracts yet
                        </Typography>
                      )}
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        setInterestMenuAnchor(null);
                        setAdhocRateChangeDialogOpen(true);
                      }}
                    >
                      Interest Rate Change %
                    </MenuItem>
                  </Menu>
                </>
              )}
              <Button
                variant="outlined"
                color="warning"
                startIcon={<FinaliseIcon />}
                onClick={() => setFinalisationDialogOpen(true)}
              >
                Finalise
              </Button>
              {contract.maturity_date && new Date(contract.maturity_date) <= new Date() && (
                <Button
                  variant="outlined"
                  color="secondary"
                  startIcon={<MatureIcon />}
                  onClick={handleMature}
                  disabled={matureSubmitting}
                >
                  {matureSubmitting ? 'Processing...' : 'Mark as Matured'}
                </Button>
              )}
            </>
          )}
          {canApproveContract && contract.status === 'Matured' && (
            <>
              {!contract.amendment_status && (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<ExtendIcon />}
                  onClick={() => setExtendDialogOpen(true)}
                >
                  Extend
                </Button>
              )}
              <Button
                variant="outlined"
                color="warning"
                startIcon={<FinaliseIcon />}
                onClick={() => setFinalisationDialogOpen(true)}
              >
                Finalise
              </Button>
            </>
          )}
          {canApproveContract && contract.status === 'Finalised' && !contract.amendment_status && (
            <Tooltip title={reserveRemaining > 0.005 ? '' : 'No remaining reserve to pay out — all reserved funds have been rolled or paid out.'}>
              <span>
                <Button
                  variant="outlined"
                  color="warning"
                  startIcon={<FinaliseIcon />}
                  onClick={() => setReservePayoutDialogOpen(true)}
                  disabled={!(reserveRemaining > 0.005)}
                >
                  Pay Out Reserve
                </Button>
              </span>
            </Tooltip>
          )}
          {canWrite && contract.status === 'Draft' && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => setDeleteDialogOpen(true)}
            >
              Delete
            </Button>
          )}
          {canAdmin && contract.status === 'Historical' && allowDeleteHistorical && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => { setHistoricalDeleteError(null); setHistoricalDeleteDialogOpen(true); }}
            >
              Delete Historical Contract
            </Button>
          )}
          {canAdmin && contract.status === 'Historical' && allowHistoricalMerge && (
            <Button
              variant="outlined"
              startIcon={<MergeIcon />}
              onClick={() => setMergeHistoricalDialogOpen(true)}
            >
              Merge Historical Contract
            </Button>
          )}
          {canAdmin && contract.status === 'Historical' && allowHistoricalBalanceOverride && (
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<OverrideIcon />}
              onClick={() => setBalanceOverrideDialogOpen(true)}
            >
              Amend
            </Button>
          )}
          {canAdmin && ['Draft', 'Pending Approval', 'Approved'].includes(contract.status) && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<CancelContractIcon />}
              onClick={() => setCancelDialogOpen(true)}
            >
              Cancel Contract
            </Button>
          )}
        </Box>
      </Box>
      {/* Pending Rate Change Banner */}
      {pendingRateChange && (
        <Alert
          severity="warning"
          sx={{ mb: 3 }}
          action={
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                color="info"
                size="small"
                variant="outlined"
                onClick={() => setRateChangePreviewOpen(true)}
              >
                Preview Impact
              </Button>
              {canAdmin && (
                <Button
                  color="primary"
                  size="small"
                  variant="contained"
                  onClick={() => navigate('/approvals')}
                >
                  Approve in Approvals
                </Button>
              )}
            </Box>
          }
        >
          <Typography variant="body2">
            <strong>Rate Change Pending:</strong>{' '}
            {pendingRateChange.old_rate}% → {pendingRateChange.new_rate}%{' '}
            (effective {format(new Date(pendingRateChange.effective_date), 'dd MMM yyyy')})
            {pendingRateChange.reason && ` — ${pendingRateChange.reason}`}
          </Typography>
        </Alert>
      )}
      {/* Matured Contract Action Required Banner.
          Driven by the server needs_action flag (past maturity, not yet resolved,
          not mid-amendment) rather than status === 'Matured' alone, so an Active
          contract past its maturity date is flagged too. Suppressed once a rollover/
          finalisation amendment is pending (the Pending Amendment banner covers that). */}
      {contract.needs_action && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>Action Required:</strong>{' '}
            This contract matured on {format(new Date(contract.maturity_date), 'dd MMM yyyy')}.
            Please <strong>Extend</strong> or <strong>Finalise</strong> this contract.
          </Typography>
        </Alert>
      )}
      {/* Pending Amendment Banner */}
      {contract.amendment_status && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>{contract.amendment_status}:</strong>{' '}
            This contract has a pending amendment awaiting approval. New amendments cannot be submitted until the current one is resolved.
          </Typography>
        </Alert>
      )}
      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
          <Tab label="Contract Details" />
          <Tab label="Amortization Schedule" />
          <Tab label="Documents" />
          <Tab label="Payment History" />
          <Tab label="Contract History" />
          {(scheduleVersions.length > 1 || adhocCapitalEvents.length > 0) && (
            <Tab label="Schedule History" />
          )}
        </Tabs>
      </Paper>
      {/* Tab: Contract Details */}
      <TabPanel value={tabValue} index={0}>
        <Grid container spacing={3}>
          {/* Basic Information */}
          <Grid size={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Basic Information & Audit
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={2}>
                  <Grid
                    size={{
                      xs: 12,
                      md: 6
                    }}>
                    <Typography variant="caption" color="textSecondary">Investor / Counterparty</Typography>
                    <Typography variant="body1">
                      {contract.is_multi_investor ? (
                        <Chip
                          label={`Multiple Investors (${investors.length})`}
                          size="small"
                          color="info"
                        />
                      ) : (
                        contract.investor_name
                      )}
                    </Typography>
                  </Grid>
                  <Grid
                    size={{
                      xs: 12,
                      md: 6
                    }}>
                    <Typography variant="caption" color="textSecondary">Product Type</Typography>
                    <Typography variant="body1">
                      <Chip label={contract.product_type} size="small" />
                    </Typography>
                  </Grid>
                  <Grid
                    size={{
                      xs: 12,
                      md: 4
                    }}>
                    <Typography variant="caption" color="textSecondary">Capital Amount</Typography>
                    <Typography variant="h6">
                      {contract.currency_code} {formatNumber(contract.capital_amount)}
                    </Typography>
                  </Grid>
                  <Grid
                    size={{
                      xs: 12,
                      md: 4
                    }}>
                    <Typography variant="caption" color="textSecondary">Currency</Typography>
                    <Typography variant="body1">{contract.currency_code}</Typography>
                  </Grid>
                  <Grid
                    size={{
                      xs: 12,
                      md: 4
                    }}>
                    <Typography variant="caption" color="textSecondary">Calculation Profile</Typography>
                    <Typography variant="body1">{contract.calculation_profile_name || `${contract.interest_calc_type} ${contract.day_count_basis || '365'}`}</Typography>
                  </Grid>
                  {/* T3-032: list ALL rollover sources (many-to-1) when present; fall back
                      to the single legacy mirror otherwise. */}
                  {rolloverSources.length > 0 && !contract.is_multi_investor && (
                    <Grid size={{ xs: 12, md: 4 }}>
                      <Typography variant="caption" color="textSecondary">
                        Rolled From{rolloverSources.length > 1 ? ` (${rolloverSources.length} contracts)` : ''}
                      </Typography>
                      {rolloverSources.map((s) => (
                        <Typography key={s.source_contract_id} variant="body1">
                          <Typography
                            component="span"
                            variant="body1"
                            color="primary"
                            sx={{ cursor: 'pointer', textDecoration: 'underline' }}
                            onClick={() => navigate(`/contracts/${s.source_contract_id}`)}
                          >
                            {s.reference}
                          </Typography>
                          {s.user_reference && (
                            <Typography component="span" variant="body2" color="textSecondary"> ({s.user_reference})</Typography>
                          )}
                          <Typography component="span" variant="body2" color="textSecondary">
                            {' '}— {contract.currency_code} {Number(s.rolled_amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </Typography>
                        </Typography>
                      ))}
                    </Grid>
                  )}
                  {rolloverSources.length === 0 && contract.rolled_from_contract_id && !contract.is_multi_investor && (
                    <Grid
                      size={{
                        xs: 12,
                        md: 4
                      }}>
                      <Typography variant="caption" color="textSecondary">Rolled From</Typography>
                      <Typography variant="body1">
                        <Typography
                          component="span"
                          variant="body1"
                          color="primary"
                          sx={{ cursor: 'pointer', textDecoration: 'underline' }}
                          onClick={() => navigate(`/contracts/${contract.rolled_from_contract_id}`)}
                        >
                          {contract.rolled_from_reference}
                        </Typography>
                        {contract.rolled_from_user_reference && (
                          <Typography component="span" variant="body2" color="textSecondary"> ({contract.rolled_from_user_reference})</Typography>
                        )}
                      </Typography>
                    </Grid>
                  )}
                  {contract.rolled_into_contracts && contract.rolled_into_contracts.length > 0 && (
                    <Grid
                      size={{
                        xs: 12,
                        md: 4
                      }}>
                      <Typography variant="caption" color="textSecondary">
                        Rolled Into{contract.rolled_into_contracts.length > 1 ? ` (${contract.rolled_into_contracts.length} contracts)` : ''}
                      </Typography>
                      {contract.rolled_into_contracts.map((child) => (
                        <Typography key={child.id} variant="body1">
                          <Typography
                            component="span"
                            variant="body1"
                            color="primary"
                            sx={{ cursor: 'pointer', textDecoration: 'underline' }}
                            onClick={() => navigate(`/contracts/${child.id}`)}
                          >
                            {child.reference}
                          </Typography>
                          {child.user_reference && (
                            <Typography component="span" variant="body2" color="textSecondary"> ({child.user_reference})</Typography>
                          )}
                        </Typography>
                      ))}
                    </Grid>
                  )}
                </Grid>

                {/* Created By / Approved By */}
                <Divider sx={{ my: 2 }} />
                <Grid container spacing={2}>
                  <Grid
                    size={{
                      xs: 12,
                      md: 4
                    }}>
                    <Typography variant="caption" color="textSecondary">Created By</Typography>
                    <Typography variant="body1">{contract.created_by || '-'}</Typography>
                    {contract.created_at && (
                      <Typography variant="caption" color="textSecondary">
                        {format(new Date(contract.created_at), 'dd MMM yyyy HH:mm')}
                      </Typography>
                    )}
                  </Grid>
                  <Grid
                    size={{
                      xs: 12,
                      md: 4
                    }}>
                    <Typography variant="caption" color="textSecondary">Approved By</Typography>
                    <Typography variant="body1">{contract.approved_by || '-'}</Typography>
                    {contract.approved_at && (
                      <Typography variant="caption" color="textSecondary">
                        {format(new Date(contract.approved_at), 'dd MMM yyyy HH:mm')}
                      </Typography>
                    )}
                  </Grid>
                  {contract.updated_by && (
                    <Grid
                      size={{
                        xs: 12,
                        md: 4
                      }}>
                      <Typography variant="caption" color="textSecondary">Last Updated By</Typography>
                      <Typography variant="body1">{contract.updated_by}</Typography>
                      {contract.updated_at && (
                        <Typography variant="caption" color="textSecondary">
                          {format(new Date(contract.updated_at), 'dd MMM yyyy HH:mm')}
                        </Typography>
                      )}
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Investors Section - for multi-investor contracts */}
          {contract.is_multi_investor && investors.length > 0 && (
            <Grid size={12}>
              <ContractInvestorsSection
                contractId={id}
                investors={investors}
                disabled={true}
                currencyCode={contract.currency_code}
                rolledFromContractId={contract.rolled_from_contract_id}
                rolledFromReference={contract.rolled_from_reference}
                rolledFromUserReference={contract.rolled_from_user_reference}
                onUpdateInvestorLTR={async (investorId, newValue) => {
                  try {
                    await apiPut(`/contracts/${id}/investors/${investorId}/likelihood-to-roll`, {
                      likelihood_to_roll: newValue || null,
                    });
                    setInvestors(prev => prev.map(inv =>
                      inv.investor_id === investorId
                        ? { ...inv, likelihood_to_roll: newValue || null }
                        : inv
                    ));
                  } catch (err) {
                    logger.error('Error updating investor LTR', { message: err.message, status: err.response?.status });
                    notify.error('Failed to update likelihood to roll');
                  }
                }}
              />
            </Grid>
          )}

          {/* Investment Term */}
          <Grid
            size={{
              xs: 12,
              md: 6
            }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Investment Term & Dates
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={2}>
                  <Grid size={6}>
                    <Typography variant="caption" color="textSecondary">Investment Term</Typography>
                    <Typography variant="body1">
                      {contract.investment_term_value} {contract.investment_term}
                    </Typography>
                  </Grid>
                  <Grid size={6}>
                    <Typography variant="caption" color="textSecondary">Start Date</Typography>
                    <Typography variant="body1">
                      {format(new Date(contract.deposit_date), 'dd MMM yyyy')}
                    </Typography>
                  </Grid>
                  <Grid size={6}>
                    <Typography variant="caption" color="textSecondary">Maturity Date</Typography>
                    <Typography variant="body1">
                      {format(new Date(contract.maturity_date), 'dd MMM yyyy')}
                    </Typography>
                  </Grid>
                  {/* Terminated early: the contract was closed out BEFORE its maturity date, which is
                      left untouched by finalisation. Without this the screen shows a Finalised
                      contract maturing years in the future with nothing saying it already ended.
                      Rendered ONLY when the two dates differ, so an at-maturity finalisation -- the
                      common case -- adds no row. Compared on the yyyy-MM-dd rendering rather than the
                      raw values because the two arrive with different time components. */}
                  {contract.finalisation_date
                    && format(new Date(contract.finalisation_date), 'yyyy-MM-dd')
                       !== format(new Date(contract.maturity_date), 'yyyy-MM-dd') && (
                    <Grid size={6}>
                      <Typography variant="caption" color="textSecondary">Terminated</Typography>
                      <Typography variant="body1">
                        {format(new Date(contract.finalisation_date), 'dd MMM yyyy')}
                        <Chip
                          label="Closed before maturity"
                          size="small"
                          color="warning"
                          sx={{ ml: 1 }}
                        />
                      </Typography>
                    </Grid>
                  )}
                  <Grid size={6}>
                    <Typography variant="caption" color="textSecondary">Funds Received Date</Typography>
                    <Typography variant="body1">
                      {contract.funds_received_date ? (
                        <>
                          {format(new Date(contract.funds_received_date), 'dd MMM yyyy')}
                          {contract.funds_received_date !== contract.deposit_date && (
                            <Chip
                              label="Interest accrues from this date"
                              size="small"
                              color="info"
                              sx={{ ml: 1 }}
                            />
                          )}
                        </>
                      ) : (
                        <Typography component="span" color="textSecondary">
                          Same as deposit date
                        </Typography>
                      )}
                    </Typography>
                  </Grid>
                  <Grid size={6}>
                    <Typography variant="caption" color="textSecondary">First Payment Date</Typography>
                    <Typography variant="body1">
                      {contract.first_payment_date ? (
                        <>
                          {format(new Date(contract.first_payment_date), 'dd MMM yyyy')}
                          <Chip
                            label="Anchors payment cycle"
                            size="small"
                            color="info"
                            sx={{ ml: 1 }}
                          />
                        </>
                      ) : (
                        <Typography component="span" color="textSecondary">
                          Same as start date
                        </Typography>
                      )}
                    </Typography>
                  </Grid>
                  <Grid size={6}>
                    <Typography variant="caption" color="textSecondary">Eligible for Re-pricing</Typography>
                    <Typography variant="body1">
                      {contract.eligible_for_repricing
                        ? `Yes (${contract.repricing_frequency || '—'})`
                        : 'No'}
                    </Typography>
                  </Grid>
                  {contract.eligible_for_repricing && (
                    <Grid size={6}>
                      <Typography variant="caption" color="textSecondary">Next Re-pricing Date</Typography>
                      <Typography variant="body1">
                        {contract.next_repricing_date
                          ? format(new Date(contract.next_repricing_date), 'dd MMM yyyy')
                          : '—'}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Cumulative Payment Variance — only when at least one varied payment exists */}
          {varianceSummary && parseInt(varianceSummary.varied_payment_count || 0) > 0 && (
            <Grid size={{ xs: 12 }}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Cumulative Payment Variance
                  </Typography>
                  <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 1 }}>
                    Difference between cash actually received and the engine-scheduled amount across matched payments.
                    The schedule itself is unchanged — these figures reflect cash booking only.
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 6, md: 3 }}>
                      <Typography variant="caption" color="textSecondary">Varied Payments</Typography>
                      <Typography variant="h5">{varianceSummary.varied_payment_count}</Typography>
                    </Grid>
                    <Grid size={{ xs: 6, md: 3 }}>
                      <Typography variant="caption" color="textSecondary">Total Short</Typography>
                      <Typography variant="h5" color={parseFloat(varianceSummary.total_short || 0) < 0 ? 'error.main' : 'inherit'}>
                        {contract.currency_code} {formatNumber(Math.abs(parseFloat(varianceSummary.total_short || 0)))}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 6, md: 3 }}>
                      <Typography variant="caption" color="textSecondary">Total Over</Typography>
                      <Typography variant="h5" color={parseFloat(varianceSummary.total_over || 0) > 0 ? 'success.main' : 'inherit'}>
                        {contract.currency_code} {formatNumber(parseFloat(varianceSummary.total_over || 0))}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 6, md: 3 }}>
                      <Typography variant="caption" color="textSecondary">Net Variance</Typography>
                      <Typography
                        variant="h5"
                        fontWeight="bold"
                        color={parseFloat(varianceSummary.net_variance || 0) < 0 ? 'error.main' : (parseFloat(varianceSummary.net_variance || 0) > 0 ? 'success.main' : 'inherit')}
                      >
                        {parseFloat(varianceSummary.net_variance || 0) < 0 ? '-' : ''}{contract.currency_code} {formatNumber(Math.abs(parseFloat(varianceSummary.net_variance || 0)))}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Rollover Likelihood - single-investor only (multi-investor LTR is inline in investors table) */}
          {!contract.is_multi_investor && (
            <Grid
              size={{
                xs: 12,
                md: 6
              }}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Rollover Likelihood
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Grid container spacing={2}>
                    <Grid size={12}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Likelihood to Roll</InputLabel>
                        <Select
                          value={contract.likelihood_to_roll || ''}
                          label="Likelihood to Roll"
                          onChange={async (e) => {
                            const newValue = e.target.value;
                            try {
                              await apiPut(`/contracts/${id}/likelihood-to-roll`, {
                                likelihood_to_roll: newValue || null
                              });
                              setContract({ ...contract, likelihood_to_roll: newValue });
                            } catch (err) {
                              logger.error('Error updating likelihood to roll', { message: err.message, status: err.response?.status });
                              notify.error('Failed to update likelihood to roll');
                            }
                          }}
                        >
                          <MenuItem value="">None</MenuItem>
                          <MenuItem value="Likely">
                            <Chip label="Likely" size="small" color="success" sx={{ mr: 1 }} /> Likely
                          </MenuItem>
                          <MenuItem value="Middle">
                            <Chip label="Possible" size="small" color="warning" sx={{ mr: 1 }} /> Possible
                          </MenuItem>
                          <MenuItem value="Not likely">
                            <Chip label="Not likely" size="small" color="error" sx={{ mr: 1 }} /> Not likely
                          </MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Interest Information */}
          <Grid
            size={{
              xs: 12,
              md: 6
            }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Interest & Payments
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={2}>
                  <Grid size={6}>
                    <Typography variant="caption" color="textSecondary">Rate Type</Typography>
                    <Typography variant="body1">{formatRateType(contract.rate_type)}</Typography>
                  </Grid>
                  <Grid size={6}>
                    <Typography variant="caption" color="textSecondary">Initial Interest Rate</Typography>
                    <Typography variant="h6">{contract.initial_interest_rate ?? contract.interest_rate}%</Typography>
                  </Grid>
                  <Grid size={6}>
                    <Typography variant="caption" color="textSecondary">Current Interest Rate</Typography>
                    <Typography variant="h6">{contract.interest_rate}%</Typography>
                  </Grid>
                  <Grid size={6}>
                    <Typography variant="caption" color="textSecondary">Rate Update Mode</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                      <Chip
                        label={contract.rate_change_mode === 'manual' ? 'Manual rate updates only' : 'Bulk benchmark sweep'}
                        size="small"
                        color={contract.rate_change_mode === 'manual' ? 'warning' : 'primary'}
                        variant="outlined"
                      />
                      {canWrite && contract.status !== 'Draft' && (
                        <IconButton
                          size="small"
                          onClick={() => setRateChangeModeDialogOpen(true)}
                          aria-label="Change rate update mode"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  </Grid>
                  {/* T3-068: rate-change effective basis (Variable/Linked only); cadence when On Specified Date. */}
                  {contract.rate_type === 'Variable' && (
                    <Grid size={6}>
                      <Typography variant="caption" color="textSecondary">Rate Change Effective Basis</Typography>
                      <Typography variant="body1">{formatRateChangeBasis(contract.rate_change_effective_basis)}</Typography>
                    </Grid>
                  )}
                  {contract.rate_type === 'Variable' && contract.rate_change_effective_basis === 'specified_date' && (
                    <Grid size={6}>
                      <Typography variant="caption" color="textSecondary">Rate Change Schedule</Typography>
                      <Typography variant="body1">
                        {contract.rate_change_frequency || '—'}
                        {contract.first_rate_change_date && (
                          <Typography component="span" variant="body2" color="text.secondary">
                            {' '}(from {format(new Date(contract.first_rate_change_date), 'dd MMM yyyy')})
                          </Typography>
                        )}
                      </Typography>
                    </Grid>
                  )}
                  {contract.benchmark_rate_type && (
                    <Grid size={6}>
                      <Typography variant="caption" color="textSecondary">Benchmark Rate</Typography>
                      <Typography variant="body1">
                        {contract.benchmark_rate_type}: {contract.benchmark_rate_value}%
                        {(contract.rate_type === 'Variable' || contract.rate_type === 'Floating') && parseFloat(contract.rate_spread || 0) !== 0 && (
                          <Typography component="span" variant="body2" color="text.secondary">
                            {' '}({parseFloat(contract.rate_spread) >= 0 ? '+' : ''}{parseFloat(contract.rate_spread).toFixed(2)}% spread)
                          </Typography>
                        )}
                      </Typography>
                    </Grid>
                  )}
                  <Grid size={6}>
                    <Typography variant="caption" color="textSecondary">Calculation Profile</Typography>
                    <Typography variant="body1">{contract.calculation_profile_name || contract.interest_calc_type}</Typography>
                    {contract.calculation_profile_formula && (
                      <Typography variant="caption" color="textSecondary" sx={{ fontFamily: 'monospace' }}>
                        {contract.calculation_profile_formula}
                      </Typography>
                    )}
                  </Grid>
                  <Grid size={6}>
                    <Typography variant="caption" color="textSecondary">Interest Payment Frequency</Typography>
                    <Typography variant="body1">{contract.interest_paid_frequency}</Typography>
                  </Grid>
                  <Grid size={6}>
                    <Typography variant="caption" color="textSecondary">Capital Payment Frequency</Typography>
                    <Typography variant="body1">{contract.capital_paid_frequency}</Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Broker Information */}
          {contract.is_brokered && (
            <Grid
              size={{
                xs: 12,
                md: 6
              }}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Broker Information
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Grid container spacing={2}>
                    <Grid size={12}>
                      <Typography variant="caption" color="textSecondary">Broker</Typography>
                      <Typography variant="body1">{contract.broker_name}</Typography>
                    </Grid>
                    <Grid size={6}>
                      {/* T3-012 / T3-084: shared presentation — VAT breakdown when VAT
                          applies, a single amount + "No-VAT" tag under the override. */}
                      <Typography variant="caption" color="textSecondary">
                        {formatBrokerFee(contract, contract.currency_code).label}
                      </Typography>
                      <Typography variant="body1" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                        {formatBrokerFee(contract, contract.currency_code).value}
                        {formatBrokerFee(contract, contract.currency_code).isNoVat && (
                          <Chip label="No-VAT" size="small" variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />
                        )}
                      </Typography>
                    </Grid>
                    {/* T3-084: a brokered fee showing 0% VAT is exactly the anomaly a reviewer
                        should be able to explain, so surface the treatment and its reason on
                        the permanent record. Only rendered for the override — a Standard-Rate
                        contract reads exactly as it did before. */}
                    {contract.broker_fee_vat_treatment === 'NO_VAT' && (
                      <Grid size={12}>
                        <Typography variant="caption" color="textSecondary">VAT Treatment</Typography>
                        <Typography variant="body1">
                          No-VAT (0%)
                          {contract.broker_fee_settlement_currency_code
                            ? ` — settled in ${contract.broker_fee_settlement_currency_code}`
                            : ''}
                          {contract.broker_fee_settled_in_other_ccy ? ' (different currency)' : ''}
                        </Typography>
                        {contract.broker_fee_vat_note && (
                          <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
                            {contract.broker_fee_vat_note}
                          </Typography>
                        )}
                      </Grid>
                    )}
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Stamp Duty (T113: standalone, multi-row — one entry per stamp-duty
              row, shown whenever present, brokered or not. Falls back to the
              legacy single column if the array is absent.) */}
          {(() => {
            const stampDuties = Array.isArray(contract.stamp_duties) && contract.stamp_duties.length > 0
              ? contract.stamp_duties
              : (contract.stamp_duty && contract.stamp_duty_fees
                  ? [{ reference: 'Stamp Duty', percentage: contract.stamp_duty_fees }]
                  : []);
            if (stampDuties.length === 0) return null;
            return (
              <Grid
                size={{
                  xs: 12,
                  md: 6
                }}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Stamp Duty
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Grid container spacing={2}>
                      {stampDuties.map((sd, i) => {
                        const pct = parseFloat(sd.percentage);
                        const amount = isFinite(pct) ? (parseFloat(contract.capital_amount) || 0) * pct / 100 : null;
                        return (
                          <Grid size={6} key={sd.id ?? i}>
                            <Typography variant="caption" color="textSecondary">{sd.reference || 'Stamp Duty'}</Typography>
                            <Typography variant="body1">
                              {formatRatePct(pct)}%{amount != null ? ` (${contract.currency_code} ${formatNumber(amount)})` : ''}
                            </Typography>
                          </Grid>
                        );
                      })}
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            );
          })()}

          {/* Issuer / Investor Group Information */}
          {contract.issuer_id && (
            <Grid
              size={{
                xs: 12,
                md: 6
              }}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {issuerLabel}
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Grid container spacing={2}>
                    <Grid size={12}>
                      <Typography variant="caption" color="textSecondary">{issuerLabel}</Typography>
                      <Typography variant="body1">{contract.issuer_name || '—'}</Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Collateral Information — loops over contract.collaterals when populated,
              falls back to the legacy single-collateral columns otherwise so contracts
              that pre-date the multi-collateral backfill still display. */}
          {contract.has_collateral && (() => {
            const rows = Array.isArray(contract.collaterals) && contract.collaterals.length > 0
              ? contract.collaterals
              : (contract.collateral_provider_id ? [{
                  id: 'legacy',
                  collateral_provider_name: contract.collateral_provider_name,
                  secured: true,
                  collateral_type: contract.collateral_type,
                  collateral_amount: contract.collateral_amount,
                  collateral_multiplier: contract.collateral_multiplier,
                  nominal_amount: contract.nominal_amount,
                  collateral_in_favour_of: contract.collateral_in_favour_of,
                  collateral_description: contract.collateral_description,
                }] : []);

            if (rows.length === 0) return null;

            return (
              <Grid size={{ xs: 12, md: 6 }}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Collateral / Encumbrance ({rows.length})
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    {rows.map((row, idx) => (
                      <Box key={row.id ?? idx} sx={{ mb: idx < rows.length - 1 ? 3 : 0, pb: idx < rows.length - 1 ? 2 : 0, borderBottom: idx < rows.length - 1 ? '1px dashed' : 'none', borderColor: 'divider' }}>
                        <Grid container spacing={2}>
                          <Grid size={12}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                              <Box>
                                <Typography variant="caption" color="textSecondary">Provider</Typography>
                                <Typography variant="body1">{row.collateral_provider_name || '—'}</Typography>
                              </Box>
                              <Chip
                                label={row.secured === false ? 'Unsecured' : 'Secured'}
                                color={row.secured === false ? 'default' : 'success'}
                                size="small"
                                variant={row.secured === false ? 'outlined' : 'filled'}
                              />
                            </Box>
                          </Grid>
                          <Grid size={6}>
                            <Typography variant="caption" color="textSecondary">Cover Value</Typography>
                            <Typography variant="body1">
                              {(row.currency_code || contract.currency_code)} {formatNumber(row.collateral_amount)}
                            </Typography>
                          </Grid>
                          {row.secured !== false && (
                            <>
                              <Grid size={6}>
                                <Typography variant="caption" color="textSecondary">Type</Typography>
                                <Typography variant="body1">{row.collateral_type || '—'}</Typography>
                              </Grid>
                              {row.collateral_multiplier != null && (
                                <Grid size={6}>
                                  <Typography variant="caption" color="textSecondary">Multiplier</Typography>
                                  <Typography variant="body1">{parseFloat(row.collateral_multiplier).toFixed(4)}</Typography>
                                </Grid>
                              )}
                              {row.nominal_amount != null && (
                                <Grid size={6}>
                                  <Typography variant="caption" color="textSecondary">Nominal / Face Value</Typography>
                                  <Typography variant="body1">
                                    {(row.currency_code || contract.currency_code)} {formatNumber(row.nominal_amount)}
                                  </Typography>
                                </Grid>
                              )}
                              {row.collateral_in_favour_of && (
                                <Grid size={12}>
                                  <Typography variant="caption" color="textSecondary">In Favour Of</Typography>
                                  <Typography variant="body1">{row.collateral_in_favour_of}</Typography>
                                </Grid>
                              )}
                              {row.collateral_description && (
                                <Grid size={12}>
                                  <Typography variant="caption" color="textSecondary">Description</Typography>
                                  <Typography variant="body2">{row.collateral_description}</Typography>
                                </Grid>
                              )}
                            </>
                          )}
                        </Grid>
                      </Box>
                    ))}
                  </CardContent>
                </Card>
              </Grid>
            );
          })()}
        </Grid>
      </TabPanel>
      {/* Tab: Amortization Schedule */}
      <TabPanel value={tabValue} index={1}>
        {scheduleVersions.length > 1 && (
          <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
            <FormControl size="small" sx={{ minWidth: 300 }}>
              <InputLabel>Schedule Version</InputLabel>
              <Select
                value={selectedVersionId || 'current'}
                onChange={(e) => handleVersionChange(e.target.value === 'current' ? null : e.target.value)}
                label="Schedule Version"
              >
                <MenuItem value="current">Current Schedule</MenuItem>
                {scheduleVersions.map((v) => (
                  <MenuItem key={v.id} value={v.id}>
                    v{v.version_number} — {v.reason || 'Original'} ({v.interest_rate}%)
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {selectedVersionId && (
              <Chip label="Viewing Historical Version" color="warning" size="small" />
            )}
            {loadingVersion && <CircularProgress size={20} />}
          </Box>
        )}
        {selectedVersionId && (() => {
          const selectedVersion = scheduleVersions.find(v => v.id === selectedVersionId);
          return selectedVersion ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Version {selectedVersion.version_number}</strong>
                {selectedVersion.reason && ` — ${selectedVersion.reason}`}
                {' | '}Rate: {selectedVersion.interest_rate}%
                {selectedVersion.effective_from && ` | Effective: ${format(new Date(selectedVersion.effective_from), 'dd MMM yyyy')}`}
                {selectedVersion.effective_to && ` to ${format(new Date(selectedVersion.effective_to), 'dd MMM yyyy')}`}
              </Typography>
            </Alert>
          ) : null;
        })()}
        <AmortizationScheduleTable
          schedule={versionSchedule || schedule}
          variances={varianceById}
          contractId={id}
          contract={contract}
          onRegenerate={contract?.status === 'Draft' ? handleRegenerateSchedule : undefined}
          canAdmin={canAdmin}
          onDownloadPDF={handleDownloadAmortizationPDF}
          isGeneratingPDF={isGenerating}
          readOnly={!!selectedVersionId}
          onOverrideRequest={(entry) => {
            setSelectedOverrideEntry(entry);
            setOverrideDialogOpen(true);
          }}
          onShowOverrides={(entry) => {
            setOverridesModalEntry(entry);
            setOverridesModalOpen(true);
          }}
        />
      </TabPanel>
      {/* Tab: Documents */}
      <TabPanel value={tabValue} index={2}>
        <DocumentUploadSection
          contractId={id}
          investorId={contract?.investor_id}
          title="Contract Documents"
          collapsible={false}
        />
      </TabPanel>
      {/* Tab: Payment History */}
      <TabPanel value={tabValue} index={3}>
        {/* Capital Receipts Section */}
        {receipts.length > 0 && (
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Capital Receipts ({receipts.length})
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date Received</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell>Reference</TableCell>
                    <TableCell>Method</TableCell>
                    <TableCell>Notes</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {receipts.map((receipt) => (
                    <TableRow key={receipt.id}>
                      <TableCell>
                        {receipt.received_date ? format(new Date(receipt.received_date), 'dd MMM yyyy') : '-'}
                      </TableCell>
                      <TableCell align="right">
                        <Typography fontWeight="medium" color="success.main">
                          {receipt.currency || contract?.currency_code} {formatNumber(receipt.amount)}
                        </Typography>
                      </TableCell>
                      <TableCell>{receipt.reference || '-'}</TableCell>
                      <TableCell>{receipt.payment_method || 'EFT'}</TableCell>
                      <TableCell>{receipt.notes || '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}

        {/* Matched Payments Section */}
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Payments ({payments.length})
          </Typography>
          {payments.length === 0 ? (
            <Typography color="textSecondary" align="center" sx={{ py: 3 }}>
              No payments recorded for this contract
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Payment Date</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell>Reference</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Matched To</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {payments.map((payment) => (
                    <TableRow key={payment.id}>
                      <TableCell>
                        {payment.payment_date ? format(new Date(payment.payment_date), 'dd MMM yyyy') : '-'}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={payment.payment_type}
                          size="small"
                          color={
                            payment.payment_type === 'Interest' ? 'info' :
                            payment.payment_type === 'Capital' ? 'success' :
                            payment.payment_type === 'Full Settlement' ? 'warning' : 'default'
                          }
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Typography fontWeight="medium">
                          {contract?.currency_code} {formatNumber(payment.payment_amount)}
                        </Typography>
                      </TableCell>
                      <TableCell>{payment.payment_reference || '-'}</TableCell>
                      <TableCell>
                        <Chip
                          label={payment.payment_status}
                          size="small"
                          color={
                            payment.payment_status === 'Matched' ? 'success' :
                            payment.payment_status === 'Pending' ? 'warning' :
                            payment.payment_status === 'Failed' ? 'error' : 'default'
                          }
                        />
                      </TableCell>
                      <TableCell>
                        {payment.schedule_id ? (
                          <Typography variant="caption">
                            Schedule #{payment.schedule_id}
                            {payment.scheduled_date && ` (${format(new Date(payment.scheduled_date), 'dd MMM yyyy')})`}
                          </Typography>
                        ) : '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>

        {/* Show message if no payments and no receipts */}
        {payments.length === 0 && receipts.length === 0 && (
          <Paper sx={{ p: 3, mt: 2, textAlign: 'center' }}>
            <Typography color="textSecondary">
              No payment history for this contract yet
            </Typography>
          </Paper>
        )}
      </TabPanel>
      {/* Tab: Contract History */}
      <TabPanel value={tabValue} index={4}>
        <Paper sx={{ p: 3 }}>
          {approvalHistory.length === 0 ? (
            <Typography color="textSecondary" align="center">
              No contract history available
            </Typography>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Date/Time</TableCell>
                    <TableCell>Action</TableCell>
                    <TableCell>User</TableCell>
                    <TableCell>Status Change</TableCell>
                    <TableCell>Comments</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {approvalHistory.map((entry) => (
                    <TableRow key={entry.id}>
                      <TableCell>
                        {entry.timestamp ? format(new Date(entry.timestamp), 'dd MMM yyyy HH:mm') : '-'}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={entry.description}
                          size="small"
                          color={
                            entry.action === 'approve'
                              ? 'success'
                              : ['reject', 'early_termination', 'matured_termination', 'cancel'].includes(entry.action)
                              ? 'error'
                              : entry.action === 'submit_for_approval'
                              ? 'warning'
                              : ['activate', 'extension', 'rollover', 'finalisation', 'contract_finalisation_submitted', 'finalisation_approved', 'reserve_payout_submitted', 'reserve_payout_approved'].includes(entry.action)
                              ? 'info'
                              : 'default'
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{entry.user?.name || 'Unknown'}</Typography>
                        {entry.user?.email && (
                          <Typography variant="caption" color="textSecondary">
                            {entry.user.email}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {entry.old_status && entry.new_status ? (
                          <Typography variant="body2">
                            {entry.old_status} → {entry.new_status}
                          </Typography>
                        ) : entry.new_status ? (
                          <Typography variant="body2">{entry.new_status}</Typography>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell>
                        {entry.comments ? (
                          <Typography variant="body2" sx={{ maxWidth: 200, wordWrap: 'break-word' }}>
                            {entry.comments}
                          </Typography>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </TabPanel>
      {/* Tab: Schedule History */}
      {(scheduleVersions.length > 1 || adhocCapitalEvents.length > 0) && (
        <TabPanel value={tabValue} index={5}>
          {/* Adhoc Capital Events */}
          {adhocCapitalEvents.length > 0 && (
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Scheduled Capital Movements
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Event Date</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell align="right">Amount</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {adhocCapitalEvents.map((event) => (
                      <TableRow key={event.id}>
                        <TableCell>
                          {event.event_date ? format(new Date(event.event_date), 'dd MMM yyyy') : '-'}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={event.event_type === 'withdrawal' ? 'Withdrawal' : 'Injection'}
                            size="small"
                            color={event.event_type === 'withdrawal' ? 'warning' : 'success'}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Typography
                            fontWeight="medium"
                            color={event.event_type === 'withdrawal' ? 'warning.main' : 'success.main'}
                          >
                            {event.event_type === 'withdrawal' ? '-' : '+'}{contract?.currency_code} {formatNumber(event.amount)}
                          </Typography>
                        </TableCell>
                        <TableCell>{event.description || '-'}</TableCell>
                        <TableCell>
                          <Chip
                            label={event.is_processed ? 'Applied' : 'Pending'}
                            size="small"
                            color={event.is_processed ? 'default' : 'info'}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}

          {/* Schedule Versions */}
          {scheduleVersions.length > 0 && (
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Schedule Version History
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Historical schedule versions created when interest rates change
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Version</TableCell>
                      <TableCell>Effective From</TableCell>
                      <TableCell>Effective To</TableCell>
                      <TableCell align="right">Interest Rate</TableCell>
                      <TableCell>Reason</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {scheduleVersions.map((version) => (
                      <TableRow key={version.id}>
                        <TableCell>
                          <Typography fontWeight="medium">
                            v{version.version_number}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {version.effective_from ? format(new Date(version.effective_from), 'dd MMM yyyy') : '-'}
                        </TableCell>
                        <TableCell>
                          {version.effective_to ? format(new Date(version.effective_to), 'dd MMM yyyy') : '-'}
                        </TableCell>
                        <TableCell align="right">
                          <Typography fontWeight="medium">
                            {version.interest_rate}%
                          </Typography>
                        </TableCell>
                        <TableCell>{version.reason || '-'}</TableCell>
                        <TableCell>
                          <Chip
                            label={version.is_active ? 'Active' : 'Historical'}
                            size="small"
                            color={version.is_active ? 'success' : 'default'}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}
        </TabPanel>
      )}
      {/* Approve Dialog */}
      <Dialog open={approveDialogOpen} onClose={() => setApproveDialogOpen(false)}>
        <DialogTitle>Approve Contract</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You are about to approve this contract. You may add optional comments below.
          </DialogContentText>
          {requireActivation && (
            <Alert severity="info" sx={{ mt: 1, mb: 1 }}>
              This tenant requires a separate activation step. After approval, the contract will be in &quot;Approved&quot; status and must be activated before the amortization schedule is generated.
            </Alert>
          )}
          <TextField
            autoFocus
            margin="dense"
            label="Comments (optional)"
            fullWidth
            multiline
            rows={3}
            value={approveComments}
            onChange={(e) => setApproveComments(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleApprove} variant="contained" color="success">
            {requireActivation ? 'Approve' : 'Approve & Activate'}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)}>
        <DialogTitle>Reject Contract</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Please provide a reason for rejecting this contract:
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label="Rejection Reason"
            fullWidth
            multiline
            rows={4}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleReject} color="error" disabled={!rejectReason}>
            Reject Contract
          </Button>
        </DialogActions>
      </Dialog>
      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Contract</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this contract? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      {/* Delete Historical Contract Dialog */}
      <Dialog
        open={historicalDeleteDialogOpen}
        onClose={() => { if (!historicalDeleting) { setHistoricalDeleteDialogOpen(false); setHistoricalDeleteError(null); } }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete Historical Contract</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Are you sure you want to permanently delete this historical contract? This action cannot
            be undone. If this contract is referenced as the rolled-from source by another contract,
            the deletion will be refused.
          </DialogContentText>
          {historicalDeleteError && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {historicalDeleteError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => { setHistoricalDeleteDialogOpen(false); setHistoricalDeleteError(null); }}
            disabled={historicalDeleting}
          >
            Cancel
          </Button>
          <Button onClick={handleDeleteHistorical} color="error" disabled={historicalDeleting}>
            {historicalDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
      {/* T3-075: Merge Historical Contract (admin + tenant toggle) */}
      <MergeHistoricalDialog
        open={mergeHistoricalDialogOpen}
        survivor={contract}
        onClose={() => setMergeHistoricalDialogOpen(false)}
        onMerged={({ loser }) => {
          notify.success(`Merged "${loser.user_reference || loser.reference}" into this contract.`);
          loadContractDetails();
        }}
      />
      {/* T3-075/T3-076: Amend Historical Contract (balance + external ref; admin + tenant toggle) */}
      <HistoricalBalanceOverrideDialog
        open={balanceOverrideDialogOpen}
        contract={contract}
        onClose={() => setBalanceOverrideDialogOpen(false)}
        onSaved={() => {
          notify.success('Amendment applied.');
          loadContractDetails();
        }}
      />
      {/* Cancel Contract Dialog */}
      <Dialog open={cancelDialogOpen} onClose={() => { setCancelDialogOpen(false); setCancelReason(''); }} maxWidth="sm" fullWidth>
        <DialogTitle>Cancel Contract</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            This will permanently cancel this contract. This action cannot be undone.
          </DialogContentText>
          <TextField
            label="Cancellation Reason"
            fullWidth
            required
            multiline
            rows={3}
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            placeholder="Provide a reason for cancelling this contract"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setCancelDialogOpen(false); setCancelReason(''); }}>Back</Button>
          <Button
            onClick={handleCancelContract}
            color="error"
            variant="contained"
            disabled={!cancelReason.trim() || cancelSubmitting}
          >
            {cancelSubmitting ? 'Cancelling...' : 'Cancel Contract'}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Finalisation Dialog */}
      <FinalisationDialog
        open={finalisationDialogOpen}
        onClose={() => setFinalisationDialogOpen(false)}
        contract={contract}
        onSubmitSuccess={(status) => {
          loadContractDetails();
          notify.success(status === 'executed' ? 'Contract finalised successfully' : 'Finalisation submitted for approval');
        }}
      />
      {/* Reserve Payout Dialog — release residual (un-rolled) reserve on a Finalised contract */}
      <ReservePayoutDialog
        open={reservePayoutDialogOpen}
        onClose={() => setReservePayoutDialogOpen(false)}
        contract={contract}
        onSubmitSuccess={() => {
          loadContractDetails();
          notify.success('Reserve payout submitted for approval');
        }}
      />
      {/* Shared confirmation dialog (mark matured, regenerate schedule) */}
      <ConfirmDialog {...confirm.dialogProps} />
      {/* Rate Change Preview Modal (read-only — approve/reject lives on Approvals page) */}
      <RateChangePreviewModal
        open={rateChangePreviewOpen}
        onClose={() => setRateChangePreviewOpen(false)}
        rateChangeId={pendingRateChange?.rate_change_id}
        currencyCode={contract?.currency_code || 'ZAR'}
        showApprove={false}
      />
      {/* Ad-Hoc Capital Amendment Dialog */}
      <AdhocCapitalAmendmentDialog
        open={amendmentDialogOpen}
        onClose={() => setAmendmentDialogOpen(false)}
        contract={contract}
        initialEventType={amendmentDialogEventType}
        onSubmitSuccess={() => loadContractDetails()}
      />
      {/* Ad-Hoc Interest Payment Dialog */}
      <AdhocInterestPaymentDialog
        open={adhocInterestDialogOpen}
        onClose={() => setAdhocInterestDialogOpen(false)}
        contract={contract}
        contractId={id}
        initialMode={adhocInterestDialogMode}
        onSuccess={() => loadContractDetails()}
      />
      {/* Ad-Hoc Rate Change Dialog */}
      <AdhocRateChangeDialog
        open={adhocRateChangeDialogOpen}
        onClose={() => setAdhocRateChangeDialogOpen(false)}
        contract={contract}
        contractId={id}
        onSuccess={() => loadContractDetails()}
      />
      {/* Rate Change Mode Dialog */}
      <RateChangeModeDialog
        open={rateChangeModeDialogOpen}
        onClose={() => setRateChangeModeDialogOpen(false)}
        contract={contract}
        onSuccess={() => loadContractDetails()}
      />
      {/* Schedule Entry Override Dialog */}
      <ScheduleEntryOverrideDialog
        open={overrideDialogOpen}
        onClose={() => {
          setOverrideDialogOpen(false);
          setSelectedOverrideEntry(null);
        }}
        contract={contract}
        scheduleEntry={selectedOverrideEntry}
        onSubmitSuccess={() => loadContractDetails()}
      />
      {/* Schedule Overrides Modal (view + revert) */}
      <ScheduleOverridesModal
        open={overridesModalOpen}
        onClose={() => {
          setOverridesModalOpen(false);
          setOverridesModalEntry(null);
        }}
        contract={contract}
        scheduleEntry={overridesModalEntry}
        onChanged={() => loadContractDetails()}
      />
      {/* Extend Contract Dialog */}
      <ExtendContractDialog
        open={extendDialogOpen}
        onClose={() => setExtendDialogOpen(false)}
        contract={contract}
        onSubmitSuccess={() => loadContractDetails()}
      />
    </Box>
  );
}

export default ContractDetail;
